import { GatedQueryError } from "../components/GatedQueryError";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { listAlerts } from "../api/alerts";
import { getDashboardSummary } from "../api/dashboard";
import { getMe } from "../api/me";
import { triggerSync } from "../api/sync";
import { AlertsList } from "../components/AlertsList";
import { SubscriptionBadge } from "../components/SubscriptionBadge";
import { TopSellersTable } from "../components/TopSellersTable";
import { usePlan } from "../hooks/usePlan";

import { currentLocale, t } from "../lib/i18n";

const WINDOW_DAYS = 30;

export function Dashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { allows } = usePlan();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const summaryQuery = useQuery({
    queryKey: ["dashboard", WINDOW_DAYS],
    queryFn: () => getDashboardSummary(WINDOW_DAYS),
  });
  const alertsQuery = useQuery({
    queryKey: ["alerts", "pending"],
    queryFn: () => listAlerts({ status: "pending", limit: 50 }),
  });

  const syncMutation = useMutation({
    mutationFn: (shopId: string) => triggerSync(shopId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      // vendor 由来の仕入先は同期で自動作成される＝一覧・未割当もリロード不要で追従させる
      queryClient.invalidateQueries({ queryKey: ["po-suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["po-unassigned"] });
    },
  });

  const lastSyncAt = summaryQuery.data?.last_sync.finished_at;
  // Backend transparently clamps the window for plans < pro (returns the actual
  // window_days). Display the effective value so free users aren't misled.
  const effectiveWindowDays = summaryQuery.data?.window_days ?? WINDOW_DAYS;
  const canHistory30 = allows("history_30");

  return (
    <Page
      title={t("ダッシュボード")}
      primaryAction={{
        content: t("今すぐ同期"),
        loading: syncMutation.isPending,
        disabled: !meQuery.data,
        onAction: () => meQuery.data && syncMutation.mutate(meQuery.data.shop_id),
      }}
    >
      <Layout>
        <Layout.Section>
          {meQuery.data && (
            <Card>
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    {meQuery.data.shopify_domain}
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {t("最終同期: {at}", {
                      at: lastSyncAt
                        ? new Date(lastSyncAt).toLocaleString(
                            currentLocale() === "ja" ? "ja-JP" : currentLocale() === "en" ? "en-US" : currentLocale(),
                          )
                        : t("未同期"),
                    })}{" "}
                    {t("／ 在庫合計: {n} 個", {
                      n: summaryQuery.data?.total_inventory_units.toLocaleString() ?? "—",
                    })}
                  </Text>
                </BlockStack>
                <SubscriptionBadge subscription={meQuery.data.subscription} />
              </InlineStack>
            </Card>
          )}
        </Layout.Section>

        {syncMutation.isError && (
          <Layout.Section>
            <Banner tone="critical">
              {t("同期に失敗しました:")} {syncMutation.error.message}
            </Banner>
          </Layout.Section>
        )}

        {syncMutation.isSuccess && (
          <Layout.Section>
            <Banner tone="success">
              {t("同期が完了しました。売れ筋とアラートを再計算しました。")}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("売れ筋 TOP {n}（直近 {days} 日）", {
                n: summaryQuery.data?.top_sellers.length ?? 10,
                days: effectiveWindowDays,
              })}
            </Text>
            {!canHistory30 && (
              <Banner
                tone="info"
                action={{ content: t("プランを見る"), onAction: () => navigate("/plans") }}
              >
                {t("現在のプランでは直近7日間の集計です。30日履歴はProで解放されます")}
              </Banner>
            )}
            {summaryQuery.isLoading ? (
              <Card>
                <SkeletonBodyText lines={5} />
              </Card>
            ) : summaryQuery.data ? (
              <TopSellersTable
                rows={summaryQuery.data.top_sellers}
                windowDays={effectiveWindowDays}
                hasSynced={Boolean(lastSyncAt)}
              />
            ) : (
              <GatedQueryError
                title={t("売れ筋データの取得に失敗しました")}
                onRetry={() => void summaryQuery.refetch()}
              />
            )}
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                {t("未対応アラート（")}
                {summaryQuery.data?.pending_alerts_count ?? alertsQuery.data?.length ?? 0}
                ）
              </Text>
              <Button
                onClick={() => meQuery.data && syncMutation.mutate(meQuery.data.shop_id)}
                disabled={!meQuery.data}
                loading={syncMutation.isPending}
              >
                {t("再計算")}
              </Button>
            </InlineStack>
            {alertsQuery.isLoading ? (
              <Card>
                <SkeletonBodyText lines={3} />
              </Card>
            ) : alertsQuery.data ? (
              <AlertsList alerts={alertsQuery.data} />
            ) : (
              <GatedQueryError
                title={t("アラートの取得に失敗しました")}
                onRetry={() => void alertsQuery.refetch()}
              />
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
